import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  buildWorkflowDiagramModel,
  buildEdgePath,
  classifyEdge,
  getDiagramContentBounds,
  NODE_W,
  NODE_H,
} from '../utils/workflowDiagramLayout';

function computeFitView(positions, viewportWidth, viewportHeight) {
  const bounds = getDiagramContentBounds(positions);
  if (!bounds.width || !viewportWidth) {
    return { zoom: 1, pan: { x: 0, y: 0 } };
  }
  const fitZoom = Math.min(
    1,
    (viewportWidth - 32) / bounds.width,
    (viewportHeight - 32) / bounds.height,
  );
  return {
    zoom: fitZoom,
    pan: {
      x: (viewportWidth - bounds.width * fitZoom) / 2 - bounds.minX * fitZoom,
      y: (viewportHeight - bounds.height * fitZoom) / 2 - bounds.minY * fitZoom,
    },
  };
}

function MiniMap({ positions, edges, canvasWidth, canvasHeight, viewport, selectedCode, width = 160, height = 100 }) {
  const codes = Object.keys(positions);
  if (!codes.length) return null;

  const xs = codes.map((c) => positions[c].x);
  const ys = codes.map((c) => positions[c].y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs) + NODE_W;
  const maxY = Math.max(...ys) + NODE_H;
  const scale = Math.min(
    (width - 12) / Math.max(1, maxX - minX),
    (height - 12) / Math.max(1, maxY - minY),
  );

  return (
    <div style={styles.miniMap}>
      <svg width={width} height={height}>
        {edges.map((edge) => {
          const from = positions[edge.from];
          const to = positions[edge.to];
          if (!from || !to) return null;
          const x1 = (from.x + NODE_W / 2 - minX) * scale + 6;
          const y1 = (from.y + NODE_H - minY) * scale + 6;
          const x2 = (to.x + NODE_W / 2 - minX) * scale + 6;
          const y2 = (to.y - minY) * scale + 6;
          return (
            <line
              key={edge.id}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={edge.isPrimary ? '#3E2C6E' : '#f59e0b'}
              strokeWidth="1.5"
              opacity="0.6"
            />
          );
        })}
        {codes.map((code) => {
          const p = positions[code];
          const left = (p.x - minX) * scale + 6;
          const top = (p.y - minY) * scale + 6;
          return (
            <rect
              key={code}
              x={left}
              y={top}
              width={Math.max(10, NODE_W * scale * 0.35)}
              height={Math.max(6, NODE_H * scale * 0.35)}
              rx="3"
              fill={selectedCode === code ? '#3E2C6E' : '#a78bfa'}
              opacity="0.9"
            />
          );
        })}
        <rect
          x={(-viewport.panX / viewport.zoom - minX) * scale + 6}
          y={(-viewport.panY / viewport.zoom - minY) * scale + 6}
          width={(viewport.width / viewport.zoom) * scale}
          height={(viewport.height / viewport.zoom) * scale}
          fill="none"
          stroke="#3E2C6E"
          strokeWidth="1.5"
          opacity="0.5"
        />
      </svg>
    </div>
  );
}

export default function WorkflowDiagram({
  states = [],
  matrix = {},
  primaryNextByCode = {},
  workflowName = 'سير العمل',
  readOnly = true,
  isMobile = false,
}) {
  const viewportRef = useRef(null);
  const panRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedCode, setSelectedCode] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: 800, height: 560 });

  const model = useMemo(
    () => buildWorkflowDiagramModel(states, matrix, primaryNextByCode),
    [states, matrix, primaryNextByCode],
  );

  const stateByCode = useMemo(
    () => new Map(states.map((s) => [s.code, s])),
    [states],
  );

  const stats = useMemo(() => ({
    states: states.length,
    transitions: model.edges.length,
    primary: model.edges.filter((e) => e.isPrimary).length,
    terminals: states.filter((s) => s.is_terminal).length,
  }), [states, model.edges]);

  const selectedState = selectedCode ? stateByCode.get(selectedCode) : null;
  const outgoing = selectedCode ? (matrix[selectedCode] || []) : [];
  const incoming = selectedCode
    ? model.edges.filter((e) => e.to === selectedCode).map((e) => e.from)
    : [];

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setViewportSize({ width, height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const applyFitView = useCallback(() => {
    const { zoom: nextZoom, pan: nextPan } = computeFitView(
      model.positions,
      viewportSize.width,
      viewportSize.height,
    );
    setZoom(nextZoom);
    setPan(nextPan);
  }, [model.positions, viewportSize.width, viewportSize.height]);

  useEffect(() => {
    if (!Object.keys(model.positions).length || !viewportSize.width) return;
    applyFitView();
  }, [model.positions, viewportSize.width, viewportSize.height, applyFitView]);

  function onViewportPointerDown(e) {
    if (e.target !== viewportRef.current && !e.target.dataset?.panSurface) return;
    panRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    window.addEventListener('pointermove', onViewportPointerMove);
    window.addEventListener('pointerup', onViewportPointerUp);
  }

  function onViewportPointerMove(e) {
    if (!panRef.current) return;
    setPan({
      x: panRef.current.panX + e.clientX - panRef.current.x,
      y: panRef.current.panY + e.clientY - panRef.current.y,
    });
  }

  function onViewportPointerUp() {
    panRef.current = null;
    window.removeEventListener('pointermove', onViewportPointerMove);
    window.removeEventListener('pointerup', onViewportPointerUp);
  }

  function onWheel(e) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    setZoom((z) => Math.min(1.6, Math.max(0.35, +(z + delta).toFixed(2))));
  }

  function fitView() {
    applyFitView();
  }

  if (!states.length) {
    return (
      <div style={styles.empty}>
        <span style={styles.emptyIcon}>◇</span>
        <p>لا توجد حالات لعرض المخطط</p>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <div style={styles.toolbarStart}>
          <span style={styles.toolbarTitle}>{workflowName}</span>
          <div style={styles.stats}>
            <span style={styles.statChip}>{stats.states} حالة</span>
            <span style={styles.statChip}>{stats.transitions} انتقال</span>
            <span style={styles.statChipPrimary}>{stats.primary} مسار رئيسي</span>
            <span style={styles.statChipMuted}>{stats.terminals} نهائية</span>
          </div>
        </div>
        <div style={styles.toolbarEnd}>
          <button type="button" style={styles.toolBtn} onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))}>+</button>
          <span style={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
          <button type="button" style={styles.toolBtn} onClick={() => setZoom((z) => Math.max(0.35, +(z - 0.1).toFixed(2)))}>−</button>
          <button type="button" style={styles.toolBtn} onClick={fitView}>ملاءمة</button>
        </div>
      </div>

      <div style={{ ...styles.body, gridTemplateColumns: isMobile ? '1fr' : '1fr 240px' }}>
        <div
          ref={viewportRef}
          style={styles.viewport}
          onPointerDown={onViewportPointerDown}
          onWheel={onWheel}
          data-pan-surface="1"
        >
          <div
            data-pan-surface="1"
            style={{
              ...styles.canvas,
              width: model.canvasWidth,
              height: model.canvasHeight,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            }}
          >
            <svg
              style={styles.edgesLayer}
              width={model.canvasWidth}
              height={model.canvasHeight}
            >
              <defs>
                <linearGradient id="edgePrimary" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#3E2C6E" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
                <marker id="arrowPrimary" markerWidth="10" markerHeight="10" refX="8" refY="4" orient="auto">
                  <path d="M0,0 L8,4 L0,8 Z" fill="#5b21b6" />
                </marker>
                <marker id="arrowBranch" markerWidth="10" markerHeight="10" refX="8" refY="4" orient="auto">
                  <path d="M0,0 L8,4 L0,8 Z" fill="#d97706" />
                </marker>
                <marker id="arrowLoop" markerWidth="10" markerHeight="10" refX="8" refY="4" orient="auto">
                  <path d="M0,0 L8,4 L0,8 Z" fill="#0d9488" />
                </marker>
              </defs>
              {model.edges.map((edge) => {
                const from = model.positions[edge.from];
                const to = model.positions[edge.to];
                if (!from || !to) return null;
                const highlighted = selectedCode && (edge.from === selectedCode || edge.to === selectedCode);
                const dimmed = selectedCode && !highlighted;
                const kind = edge.from === edge.to ? 'self' : classifyEdge(from, to);
                const isLoop = kind === 'loop' || kind === 'self';
                const d = buildEdgePath(from, to, kind);
                const stroke = edge.isPrimary
                  ? 'url(#edgePrimary)'
                  : isLoop
                    ? '#14b8a6'
                    : '#fbbf24';
                const marker = edge.isPrimary
                  ? 'url(#arrowPrimary)'
                  : isLoop
                    ? 'url(#arrowLoop)'
                    : 'url(#arrowBranch)';
                return (
                  <g key={edge.id}>
                    <path
                      d={d}
                      fill="none"
                      stroke={stroke}
                      strokeWidth={highlighted ? 3.5 : edge.isPrimary ? 2.8 : isLoop ? 2.4 : 2}
                      strokeDasharray={edge.isPrimary ? undefined : isLoop ? '6 4' : '8 5'}
                      markerEnd={marker}
                      opacity={dimmed ? 0.2 : highlighted ? 1 : 0.8}
                    />
                  </g>
                );
              })}
            </svg>

            {states.map((state) => {
              const pos = model.positions[state.code];
              if (!pos) return null;
              const isSelected = selectedCode === state.code;
              const isOnPrimary = model.primaryPath.includes(state.code);
              const connected = selectedCode && (
                selectedCode === state.code
                || (matrix[selectedCode] || []).includes(state.code)
                || (matrix[state.code] || []).includes(selectedCode)
              );
              const faded = selectedCode && !connected;

              return (
                <button
                  key={state.code}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCode((prev) => (prev === state.code ? null : state.code));
                  }}
                  style={{
                    ...styles.node,
                    left: pos.x,
                    top: pos.y,
                    opacity: faded ? 0.35 : 1,
                    borderColor: isSelected ? '#3E2C6E' : 'rgba(148,163,184,0.35)',
                    boxShadow: isSelected
                      ? '0 0 0 3px rgba(62,44,110,0.2), 0 12px 28px rgba(62,44,110,0.18)'
                      : isOnPrimary
                        ? '0 8px 24px rgba(62,44,110,0.12)'
                        : '0 6px 18px rgba(15,23,42,0.08)',
                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  }}
                >
                  <div style={{ ...styles.nodeAccent, background: state.color || '#607d8b' }} />
                  <div style={styles.nodeBody}>
                    <div style={styles.nodeTop}>
                      <span style={styles.nodeName}>{state.name}</span>
                      {state.is_start && <span style={styles.startPill}>بداية</span>}
                      {state.is_terminal && <span style={styles.terminalPill}>نهائية</span>}
                    </div>
                    <code style={styles.nodeCode}>{state.code}</code>
                    {isOnPrimary && !state.is_terminal && (
                      <span style={styles.pathPill}>مسار رئيسي</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside style={styles.side}>
          <div style={styles.legend}>
            <div style={styles.legendTitle}>دليل المخطط</div>
            <div style={styles.legendRow}>
              <span style={styles.legendLinePrimary} />
              <span>مسار رئيسي</span>
            </div>
            <div style={styles.legendRow}>
              <span style={styles.legendLineBranch} />
              <span>مسار فرعي</span>
            </div>
            <div style={styles.legendRow}>
              <span style={styles.legendLineLoop} />
              <span>عودة / حلقة</span>
            </div>
          </div>

          <MiniMap
            positions={model.positions}
            edges={model.edges}
            canvasWidth={model.canvasWidth}
            canvasHeight={model.canvasHeight}
            viewport={{ panX: pan.x, panY: pan.y, zoom, width: viewportSize.width, height: viewportSize.height }}
            selectedCode={selectedCode}
          />

          {selectedState ? (
            <div style={styles.detail}>
              <div style={styles.detailHeader}>
                <span style={{ ...styles.detailBadge, background: selectedState.color }}>{selectedState.name}</span>
              </div>
              <code style={styles.detailCode}>{selectedState.code}</code>
              <div style={styles.detailFlags}>
                {selectedState.is_start && <span style={styles.startPill}>بداية</span>}
                {selectedState.is_terminal && <span style={styles.terminalPill}>نهائية</span>}
              </div>
              {incoming.length > 0 && (
                <div style={styles.detailSection}>
                  <div style={styles.detailLabel}>قادم من</div>
                  <div style={styles.chipList}>
                    {incoming.map((code) => (
                      <button
                        key={code}
                        type="button"
                        style={{ ...styles.chip, background: stateByCode.get(code)?.color || '#607d8b' }}
                        onClick={() => setSelectedCode(code)}
                      >
                        {stateByCode.get(code)?.name || code}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {outgoing.length > 0 && (
                <div style={styles.detailSection}>
                  <div style={styles.detailLabel}>ينتقل إلى</div>
                  <div style={styles.chipList}>
                    {outgoing.map((code) => {
                      const isPrimary = primaryNextByCode[selectedCode] === code;
                      return (
                        <button
                          key={code}
                          type="button"
                          style={{
                            ...styles.chip,
                            background: stateByCode.get(code)?.color || '#607d8b',
                            outline: isPrimary ? '2px solid #3E2C6E' : 'none',
                          }}
                          onClick={() => setSelectedCode(code)}
                        >
                          {stateByCode.get(code)?.name || code}
                          {isPrimary && ' ★'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {selectedState.is_terminal && (
                <p style={styles.detailNote}>حالة نهائية — لا انتقالات بعدها</p>
              )}
            </div>
          ) : (
            <div style={styles.detailEmpty}>
              <p>انقر على حالة لعرض تفاصيل الانتقالات</p>
              {readOnly && <p style={styles.hint}>عرض للقراءة فقط — عدّل من تبويبي الحالات وسير العمل</p>}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '12px' },
  toolbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
    flexWrap: 'wrap', padding: '12px 14px', background: 'linear-gradient(135deg,#faf8ff,#fff)',
    borderRadius: '12px', border: '1px solid #e8e0f5',
  },
  toolbarStart: { display: 'flex', flexDirection: 'column', gap: '8px' },
  toolbarTitle: { fontWeight: '700', color: '#3E2C6E', fontSize: '15px' },
  stats: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  statChip: { fontSize: '11px', padding: '3px 8px', borderRadius: '999px', background: '#f1f5f9', color: '#475569', fontWeight: '600' },
  statChipPrimary: { fontSize: '11px', padding: '3px 8px', borderRadius: '999px', background: '#EDE7F6', color: '#3E2C6E', fontWeight: '600' },
  statChipMuted: { fontSize: '11px', padding: '3px 8px', borderRadius: '999px', background: '#f5f5f5', color: '#888', fontWeight: '600' },
  toolbarEnd: { display: 'flex', alignItems: 'center', gap: '6px' },
  toolBtn: {
    padding: '6px 12px', borderRadius: '8px', border: '1px solid #d1c4e9',
    background: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px', color: '#3E2C6E',
  },
  zoomLabel: { fontSize: '12px', color: '#666', minWidth: '42px', textAlign: 'center', fontWeight: '600' },
  body: { display: 'grid', gridTemplateColumns: '1fr 240px', gap: '12px', minHeight: '520px' },
  viewport: {
    position: 'relative', overflow: 'hidden', borderRadius: '14px',
    background: 'radial-gradient(circle at 20% 20%, rgba(62,44,110,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(124,58,237,0.05) 0%, transparent 45%), linear-gradient(180deg,#f8fafc 0%,#f1f5f9 100%)',
    border: '1px solid #e2e8f0', minHeight: '520px', cursor: 'grab', touchAction: 'none',
  },
  canvas: {
    position: 'absolute', top: 0, left: 0, transformOrigin: '0 0',
    backgroundImage: 'radial-gradient(circle, rgba(148,163,184,0.35) 1px, transparent 1px)',
    backgroundSize: '24px 24px',
  },
  edgesLayer: { position: 'absolute', inset: 0, pointerEvents: 'none' },
  node: {
    position: 'absolute', width: `${NODE_W}px`, minHeight: `${NODE_H}px`,
    borderRadius: '14px', border: '1px solid rgba(148,163,184,0.35)',
    padding: 0, cursor: 'pointer', textAlign: 'right', background: 'rgba(255,255,255,0.97)',
    overflow: 'hidden', transition: 'box-shadow 0.15s, transform 0.15s, opacity 0.15s',
    fontFamily: 'inherit',
  },
  nodeAccent: { height: '5px', width: '100%' },
  nodeBody: { padding: '10px 12px 12px' },
  nodeTop: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' },
  nodeName: { fontWeight: '700', color: '#1e293b', fontSize: '14px', flex: 1 },
  nodeCode: { fontSize: '11px', color: '#64748b', background: '#f8fafc', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' },
  startPill: { fontSize: '10px', color: '#166534', background: '#dcfce7', padding: '2px 6px', borderRadius: '999px', fontWeight: '600' },
  terminalPill: { fontSize: '10px', color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '999px', fontWeight: '600' },
  pathPill: { fontSize: '10px', color: '#3E2C6E', marginTop: '6px', display: 'inline-block', fontWeight: '600' },
  side: { display: 'flex', flexDirection: 'column', gap: '12px' },
  legend: { background: '#fff', borderRadius: '12px', padding: '12px', border: '1px solid #e2e8f0' },
  legendTitle: { fontWeight: '700', color: '#3E2C6E', fontSize: '13px', marginBottom: '10px' },
  legendRow: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#475569', marginBottom: '6px' },
  legendLinePrimary: { width: '28px', height: '3px', borderRadius: '2px', background: 'linear-gradient(90deg,#3E2C6E,#7c3aed)' },
  legendLineBranch: { width: '28px', height: '0', borderTop: '2px dashed #f59e0b' },
  legendLineLoop: { width: '28px', height: '3px', borderRadius: '2px', background: '#14b8a6', opacity: 0.85 },
  miniMap: { background: '#fff', borderRadius: '12px', padding: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' },
  detail: { background: '#fff', borderRadius: '12px', padding: '14px', border: '1px solid #e2e8f0', flex: 1 },
  detailHeader: { marginBottom: '8px' },
  detailBadge: { color: '#fff', padding: '6px 12px', borderRadius: '999px', fontSize: '14px', fontWeight: '700', display: 'inline-block' },
  detailCode: { fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '8px' },
  detailFlags: { display: 'flex', gap: '6px', marginBottom: '12px' },
  detailSection: { marginBottom: '12px' },
  detailLabel: { fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px' },
  chipList: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip: {
    color: '#fff', border: 'none', borderRadius: '999px', padding: '5px 10px',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
  },
  detailNote: { fontSize: '12px', color: '#888', margin: '8px 0 0', lineHeight: 1.5 },
  detailEmpty: { background: '#fff', borderRadius: '12px', padding: '16px', border: '1px dashed #d1c4e9', flex: 1, textAlign: 'center' },
  hint: { fontSize: '11px', color: '#94a3b8', marginTop: '8px' },
  empty: { textAlign: 'center', padding: '48px', color: '#94a3b8' },
  emptyIcon: { fontSize: '32px', display: 'block', marginBottom: '8px', opacity: 0.5 },
};